import React, { useCallback, useEffect, useRef, useState } from "react";
import { Search } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Card } from "../../components/ui/card";
import { ContentLayout } from "../../components/admin-panel/content-layout";
import Pagination from "../../components/Pagination";
import SuperAdminService from "../../services/superAdmin";
import { toastError, toastSuccess } from "../../components/ToastNotification";
import Loader from "../../assets/images/loader.gif";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";

const SEARCH_DEBOUNCE_MS = 400;

const Inquiry = () => {
  const [inquiryData, setInquiryData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [updatingInquiryId, setUpdatingInquiryId] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const debouncedSearchRef = useRef(debouncedSearch);
  const suppressPageFetchRef = useRef(false);

  debouncedSearchRef.current = debouncedSearch;

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [searchInput]);

  const loadInquiries = useCallback(async (page, search) => {
    setIsLoading(true);
    try {
      const response = await SuperAdminService?.getInquiries(page, limit, search);
      if (response) {
        setInquiryData(response?.data?.data?.data || []);
        setTotalPages(Math.ceil(response.data?.data?.total / limit));
      }
    } catch (error) {
      toastError(error?.response?.data?.message);
    } finally {
      setIsLoading(false);
    }
  }, [limit]);

  useEffect(() => {
    suppressPageFetchRef.current = true;
    setCurrentPage(1);
    loadInquiries(1, debouncedSearch);
  }, [debouncedSearch, loadInquiries]);

  useEffect(() => {
    if (suppressPageFetchRef.current) {
      suppressPageFetchRef.current = false;
      return;
    }
    loadInquiries(currentPage, debouncedSearchRef.current);
  }, [currentPage, loadInquiries]);

  const handlePageChange = (page, event) => {
    event.preventDefault();
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleCloseInquiry = async (inquiryId) => {
    if (!inquiryId) return;

    setUpdatingInquiryId(inquiryId);
    try {
      const response = await SuperAdminService?.updateInquiryStatus(inquiryId, {
        inquiryStatus: "closed",
      });

      if (response) {
        toastSuccess(response?.data?.message || "Inquiry status updated successfully");
        setInquiryData((prev) =>
          prev.map((item) =>
            item?._id === inquiryId ? { ...item, inquiryStatus: "closed" } : item
          )
        );
      }
    } catch (error) {
      toastError(error?.response?.data?.message || "Failed to update inquiry status");
    } finally {
      setUpdatingInquiryId("");
    }
  };

  return (
    <ContentLayout>
      <Card className="grid gap-5">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <h1 className="font-semibold text-xl">Inquiry</h1>
          <div className="relative w-full sm:max-w-xs shrink-0">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/40 pointer-events-none" />
            <Input
              type="search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search inquiries…"
              className="pl-9 bg-darkGray border-border text-white placeholder:text-white/40"
            />
          </div>
        </div>
        <div className="!border-border !border rounded-lg relative w-full overflow-auto">
          <Table>
            <TableHeader>
              <TableRow className="!bg-darkGray text-white">
                <TableHead className="w-[100px] text-white">No.</TableHead>
                <TableHead className="text-white">Organization</TableHead>
                <TableHead className="text-white">User Name</TableHead>
                <TableHead className="text-white">Email</TableHead>
                <TableHead className="text-white">Phone</TableHead>
                <TableHead className="text-white">User</TableHead>
                <TableHead className="text-white">Status</TableHead>
                <TableHead className="text-white">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center h-40">
                    <div className="flex justify-center items-center">
                      <img src={Loader} alt="Loader" className="w-20" />
                    </div>
                  </TableCell>
                </TableRow>
              ) : inquiryData.length > 0 ? (
                inquiryData.map((inquiry, index) => (
                  <TableRow key={inquiry._id}>
                    <TableCell>{(currentPage - 1) * limit + index + 1}</TableCell>
                    <TableCell>{inquiry?.name || "-"}</TableCell>
                    <TableCell>{inquiry?.org?.name || "-"}</TableCell>
                    <TableCell>{inquiry?.org?.email || "-"}</TableCell>
                    <TableCell>{inquiry?.org?.mobileNumber || "-"}</TableCell>
                    <TableCell>{inquiry?.numberOfSeats || "-"}</TableCell>
                    <TableCell className="capitalize">
                      {inquiry?.inquiryStatus || "-"}
                    </TableCell>
                    <TableCell>
                      {inquiry?.inquiryStatus === "closed" ? (
                        <span className="text-muted-foreground text-sm">Closed</span>
                      ) : (
                        <Button
                          type="button"
                          size="sm"
                          onClick={() => handleCloseInquiry(inquiry?._id)}
                          disabled={updatingInquiryId === inquiry?._id}
                        >
                          {updatingInquiryId === inquiry?._id ? "Updating..." : "Close"}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={8} className="text-center h-20 font-bold">
                    Inquiry Not Found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        {inquiryData.length > 0 && (
          <div className="flex justify-end items-center">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              handlePageChange={handlePageChange}
            />
          </div>
        )}
      </Card>
    </ContentLayout>
  );
};

export default Inquiry;